import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  useFetchIndexFundHoldings,
  useUpdateIndexFundHolding,
  useDeleteIndexFundHolding,
} from '@/hooks/securities/indexfunds/useIndexFundHoldings.ts';
import { useFetchIndexFundPrice } from '@/hooks/securities/indexfunds/useFetchIndexFundPrice.ts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IndexFundForm } from '@/components/index-fund-form/IndexFundForm';
import { IndexFundDetailMetricsCards } from '@/components/index-fund-detail-metrics-cards/IndexFundDetailMetricsCards';
import { IndexFundNAVTrendChart } from '@/components/index-fund-nav-trend-chart/IndexFundNAVTrendChart';
import { PageHeaderSkeleton, AccountCardSkeleton, ChartSkeleton } from '@/components/LoadingSkeletons';
import type { IndexFundFormValues } from '@/components/index-fund-form/indexFundForm.schema';
import {IndexFundHolding} from "@/integrations/supabase/indexfunds.models.ts";

const CATEGORY_LABELS: Record<string, string> = {
  large_cap_equity: 'Large Cap Equity',
  mid_cap_equity: 'Mid Cap Equity',
  small_cap_equity: 'Small Cap Equity',
  emerging_markets: 'Emerging Markets',
  developed_markets: 'Developed Markets',
  global_equity: 'Global Equity',
  fixed_income: 'Fixed Income',
  real_estate: 'Real Estate',
  commodity: 'Commodity',
  multi_asset: 'Multi-Asset',
};

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * IndexFundDetailPage displays detailed information for a single index fund holding.
 * Accessible at /investments/securities/index-funds/:holdingId
 */
export default function IndexFundDetailPage() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const navigate = useNavigate();

  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: holdings = [], isLoading: holdingsLoading } = useFetchIndexFundHoldings();
  const updateHolding = useUpdateIndexFundHolding();
  const deleteHolding = useDeleteIndexFundHolding();

  const holding: IndexFundHolding | undefined = useMemo(
    () => holdings.find((h) => h.id === holdingId),
    [holdings, holdingId]
  );

  const { data: price = null, isLoading: priceLoading } = useFetchIndexFundPrice(
    holding?.fund_symbol ?? ''
  );

  const isLoading = holdingsLoading;

  const handleSaveHolding = async (data: IndexFundFormValues) => {
    try {
      await updateHolding.mutateAsync({ id: holdingId!, ...data });
      toast.success('Index fund holding updated');
      setEditFormOpen(false);
    } catch {
      toast.error('Failed to update index fund holding');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteHolding.mutateAsync(holdingId!);
      toast.success('Index fund holding deleted');
      navigate('/investments/securities/index-funds');
    } catch {
      toast.error('Failed to delete index fund holding');
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-6" aria-busy="true" aria-live="polite" data-testid="loading-skeleton">
          <PageHeaderSkeleton />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <AccountCardSkeleton key={i} />)}
          </div>
          <AccountCardSkeleton />
          <ChartSkeleton />
        </div>
      </ErrorBoundary>
    );
  }

  if (!holding) {
    return (
      <ErrorBoundary onReset={() => window.location.reload()}>
        <div className="space-y-4" data-testid="not-found-state">
          <p className="text-muted-foreground">Index fund holding not found.</p>
          <Button asChild variant="outline">
            <Link to="/investments/securities/index-funds">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Index Funds
            </Link>
          </Button>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary onReset={() => window.location.reload()}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/investments/securities/index-funds')}
              aria-label="Back to Index Funds overview"
              className="min-h-[44px] min-w-[44px]"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {holding.fund_symbol}
              </h1>
              <p className="text-sm text-muted-foreground sm:text-base">{holding.fund_name}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Index fund holding actions">
            <Button
              variant="outline"
              onClick={() => setEditFormOpen(true)}
              className="min-h-[44px]"
              aria-label="Edit index fund holding"
              data-testid="edit-button"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="min-h-[44px]"
              aria-label="Delete index fund holding"
              data-testid="delete-button"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Detail Metrics Cards */}
        <IndexFundDetailMetricsCards
          holding={holding}
          price={price}
          isLoading={priceLoading}
        />

        {/* Holding Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Holding Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Fund Symbol</p>
                <p className="font-medium" data-testid="field-fund-symbol">{holding.fund_symbol}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Fund Name</p>
                <p className="font-medium" data-testid="field-fund-name">{holding.fund_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Fund Category</p>
                <Badge variant="secondary" data-testid="field-fund-category">
                  {CATEGORY_LABELS[holding.fund_category] ?? holding.fund_category}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Benchmark Index</p>
                <p className="font-medium" data-testid="field-benchmark-index">{holding.benchmark_index}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Units Held</p>
                <p className="font-medium" data-testid="field-units-held">
                  {holding.units_held.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Avg Cost / Unit</p>
                <p className="font-medium" data-testid="field-avg-cost">
                  {holding.currency}{' '}
                  {holding.average_cost_per_unit.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 4,
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Current NAV</p>
                <p className="font-medium" data-testid="field-current-nav">
                  {price != null
                    ? `${price.currency} ${price.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="font-medium" data-testid="field-currency">{holding.currency}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Purchase Date</p>
                <p className="font-medium" data-testid="field-purchase-date">{formatDate(holding.purchase_date)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Expense Ratio</p>
                <p className="font-medium" data-testid="field-expense-ratio">
                  {holding.expense_ratio != null ? `${holding.expense_ratio.toFixed(2)}%` : '—'}
                </p>
              </div>
              {holding.notes && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="font-medium" data-testid="field-notes">{holding.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* NAV Trend Chart */}
        <IndexFundNAVTrendChart fundSymbol={holding.fund_symbol} period="1M" />

        {/* Edit Dialog */}
        <Dialog open={editFormOpen} onOpenChange={(open) => { setEditFormOpen(open); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full">
            <DialogHeader>
              <DialogTitle>Edit Index Fund Holding</DialogTitle>
              <DialogDescription>Update the details of your index fund holding.</DialogDescription>
            </DialogHeader>
            <IndexFundForm
              holding={holding}
              onSave={handleSaveHolding}
              onCancel={() => setEditFormOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !open && setDeleteDialogOpen(false)}>
          <AlertDialogContent className="w-[95vw] sm:w-full sm:max-w-[500px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete index fund holding?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{holding.fund_symbol}</strong>. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel
                onClick={() => setDeleteDialogOpen(false)}
                className="min-h-[44px] w-full sm:w-auto"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
